import React, { useState, useRef, useEffect } from 'react';
import './PaymentStub.css';
import { getCurrentTerms } from '../services/termsService';
import DOMPurify from 'dompurify';

function PaymentStub({ paymentData, onClose }) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [termsData, setTermsData] = useState(null);
  const [termsLoading, setTermsLoading] = useState(true);
  const [termsError, setTermsError] = useState('');
  const termsRef = useRef(null);

  const handleScroll = () => {
    if (termsRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = termsRef.current;
      // Check if scrolled to bottom (with 5px tolerance)
      if (scrollHeight - scrollTop <= clientHeight + 5) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const handleStripePayment = async () => {
    if (!paymentData || isProcessing) return;

    setIsProcessing(true);

    // Use test key in development
    const stripeKey = process.env.NODE_ENV === 'development'
      ? process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST
      : process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

    try {

      // Create the data object for the backend
      const checkoutData = {
        journeyId: paymentData.route.id, // This is the route ID which will be used as journey routeId
        journeyData: {
          name: paymentData.route.name,
          startDate: paymentData.startDate,
          endDate: paymentData.endDate,
          nights: paymentData.nights,
          price: paymentData.price,
          currency: paymentData.currency,
          storeTagline: paymentData.route.storeTagline,
          storeStatus: paymentData.route.storeStatus,
          cost: paymentData.price
        },
        cost: paymentData.price,
        dailyAllocations: paymentData.dailyAllocations || [] // Include daily allocations
      };

      console.log('Backend URL:', process.env.REACT_APP_BACKEND_URL);
      console.log('Checkout data:', checkoutData);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/create-checkout-session`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(checkoutData),
        }
      );

      console.log('Response status:', response.status);
      console.log('Response OK:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server error details:', errorText);
        throw new Error(`Server error: ${response.statusText} - ${errorText}`);
      }

      const responseData = await response.json();
      
      const { id: sessionId } = responseData;

      if (!sessionId) {
        throw new Error('No session ID received from server');
      }

      // Redirect to Stripe Checkout with the appropriate key
      const stripe = await window.Stripe(stripeKey);
      const result = await stripe.redirectToCheckout({ sessionId });
      
      if (result.error) {
        throw new Error(result.error.message);
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      console.error('Error details:', {
        backendUrl: process.env.REACT_APP_BACKEND_URL,
        stripeKey: stripeKey ? 'Set' : 'Not set',
        errorMessage: error.message
      });

      // More detailed error message
      let errorMessage = 'Error initiating payment. ';
      if (!process.env.REACT_APP_BACKEND_URL) {
        errorMessage += 'Backend URL not configured. ';
      }
      if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY && !process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST) {
        errorMessage += 'Stripe key not configured. ';
      }
      errorMessage += 'Please check console for details.';

      alert(errorMessage);
      setIsProcessing(false);
    }
  };

  // Load terms data when component mounts
  useEffect(() => {
    const loadTermsData = async () => {
      try {
        setTermsLoading(true);
        setTermsError('');
        const terms = await getCurrentTerms();
        setTermsData(terms);
      } catch (err) {
        console.error('Error loading terms:', err);
        setTermsError(err.message || 'Failed to load terms and conditions');
        setTermsData(null);
      } finally {
        setTermsLoading(false);
      }
    };

    loadTermsData();
  }, []);


  return (
    <div className="payment-stub">
      <h2>Terms and Conditions</h2>
      
      <div className="terms-container">
        <div 
          className="terms-content" 
          ref={termsRef}
          onScroll={handleScroll}
        >
          {termsLoading ? (
            <div className="terms-loading">
              <div className="loading-spinner"></div>
              <p>Loading terms and conditions...</p>
            </div>
          ) : termsError || !termsData ? (
            <div className="terms-error">
              <h3>⚠️ Terms and Conditions Unavailable</h3>
              <p>{termsError || 'Terms and conditions could not be loaded'}</p>
              <p className="error-subtext">Please contact support or try again later.</p>
            </div>
          ) : (
            <>
              {termsData?.version && (
                <p className="terms-version">Version {termsData.version}</p>
              )}
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(termsData.content) }} />
            </>
          )}
        </div>
      </div>
      
      <button 
        className={`payment-go-button ${(!hasScrolledToBottom || isProcessing || !termsData) ? 'disabled' : ''}`}
        onClick={handleStripePayment}
        disabled={!hasScrolledToBottom || isProcessing || !termsData}
        title={
          !termsData ? "Terms and conditions are not available" :
          !hasScrolledToBottom ? "Please read the terms and conditions" : 
          "Proceed to payment"
        }
      >
        {isProcessing ? 'Processing...' : 
         !termsData ? 'Terms Unavailable' : 
         `Go - ${paymentData?.currencySymbol || ''}${paymentData?.price || ''}`}
      </button>
      
      {!hasScrolledToBottom && termsData && !termsLoading && (
        <p className="scroll-hint">Please scroll to the bottom of the terms to continue</p>
      )}
    </div>
  );
}

export default PaymentStub;